import {
  getDateFormat,
  getTimeFormat,
  limitKeyFunctionMap,
} from '@griply/utils';
import { startOfDay } from 'date-fns';
import {
  getGoals,
  getLifeAreasDocs,
  getRelationshipDocs,
  getTagsDocs,
  getTaskDocs,
  getUser,
} from 'shared/services/backend-api';
import { getAuthentication } from 'shared/services/backend-api/helpers';
import { getErrorReporter } from 'shared/services/error-tracking';
import { TaskCreationData } from 'shared/types/electron';
import { Goal } from 'shared/types/goal';
import { InsightsTimeframes } from 'shared/types/insights';
import { LifeArea, LocalizedLifeArea } from 'shared/types/life-area';
import { LimitKeys } from 'shared/types/limit-keys';
import { Relationship } from 'shared/types/relationship';
import { Tag } from 'shared/types/tag';
import { TaskType } from 'shared/types/task-base';
import { User } from 'shared/types/user';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

import { IpcChannels } from './types';

type IpcResponder = { send: (channel: IpcChannels, data: unknown) => void };

export type BuildTaskCreationDataOptions = {
  user: User | null;
  habitCount: number;
  goals: Goal[];
  lifeAreas: (LifeArea | LocalizedLifeArea)[];
  tags: Tag[];
  relationships: Relationship[];
};

export const buildTaskCreationData = ({
  user,
  habitCount,
  goals,
  lifeAreas,
  tags,
  relationships,
}: BuildTaskCreationDataOptions): TaskCreationData => {
  const today = startOfDay(new Date());

  return {
    goals,
    lifeAreas,
    tags,
    relationships,

    timeframe:
      user?.settings?.habitStatisticsTimeframe ?? InsightsTimeframes.Week,
    weekStartsOn: getWeekStartsOn(user),
    timeFormat: getTimeFormat(user),
    dateFormat: getDateFormat(user),
    language: user?.settings?.language,
    createHabitAllowed: user
      ? limitKeyFunctionMap[LimitKeys.CreateHabit]({
          user,
          today,
          total: habitCount,
        })
      : false,
    createHabitTargetAllowed: user
      ? limitKeyFunctionMap[LimitKeys.HabitScheduleTarget]({
          user,
          today,
        })
      : false,
    createHabitIconAllowed: user
      ? limitKeyFunctionMap[LimitKeys.HabitIcons]({ user, today })
      : false,
    createGoalAllowed: user
      ? limitKeyFunctionMap[LimitKeys.CreateGoal]({
          user,
          today,
          totalUncompleted: goals.length,
        })
      : false,
  };
};

const emptyTaskCreationData = (): TaskCreationData =>
  buildTaskCreationData({
    user: null,
    habitCount: 0,
    goals: [],
    lifeAreas: [],
    tags: [],
    relationships: [],
  });

const sendTaskCreationData = async (ipcRenderer: IpcResponder) => {
  // the popover only settles on a response, and every read below throws without a uid
  const auth = await getAuthentication();
  if (!auth.currentUser) {
    ipcRenderer.send(
      IpcChannels.GetTaskCreationDataResponse,
      emptyTaskCreationData(),
    );
    return;
  }

  const user = await getUser().catch((error) => {
    getErrorReporter().captureException(error, {
      tags: { source: 'electron-task-creation-data-user-read' },
    });
    return null;
  });
  const habitTasks = await getTaskDocs({ type: TaskType.Habit });
  const habits =
    habitTasks?.filter(({ schedules }) => !!schedules?.length) ?? [];
  const goals = await getGoals();
  const activeGoals = goals.filter(
    ({ completedAt, archivedAt }) => !archivedAt && !completedAt,
  );
  const lifeAreas = await getLifeAreasDocs();
  const activeLifeAreas = lifeAreas.filter(({ archivedAt }) => !archivedAt);
  const tags = await getTagsDocs();
  const relationships = await getRelationshipDocs();

  ipcRenderer.send(
    IpcChannels.GetTaskCreationDataResponse,
    buildTaskCreationData({
      user,
      habitCount: habits.length,
      goals: activeGoals,
      lifeAreas: activeLifeAreas,
      tags,
      relationships,
    }),
  );
};

export const subscribeToTaskCreationData = () => {
  // @ts-ignore
  const ipcRenderer = window.ipcRenderer;

  if (!ipcRenderer) {
    return;
  }

  ipcRenderer.on(IpcChannels.GetTaskCreationDataRequest, async () => {
    try {
      await sendTaskCreationData(ipcRenderer);
    } catch (error) {
      getErrorReporter().captureException(error, {
        tags: { source: 'electron-task-creation-data' },
      });
    }
  });
};
